import styled from '@emotion/styled';
import { gettext } from '@eventbrite/i18n';
import {
    getFormValidationAdornment,
    TextField,
    Typography,
} from '@eventbrite/marmalade';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { AutoCreateEventFormValues } from '../types';

const MIN_TICKETS_ALLOWED = 1;
const MAX_TICKETS_ALLOWED = 500000;

export enum TicketsAvailableFormFieldTestId {
    Field = 'AutoCreateEventTicketsAvailableFormField',
}

export const TicketsAvailableFormField: React.FC = () => {
    const { control } = useFormContext<AutoCreateEventFormValues>();

    return (
        <>
            <Title>
                <Typography as="h2" color="neutral-900" variant="body-lg">
                    {gettext("What's the capacity for your event?")}
                </Typography>
            </Title>
            <Subtitle>
                <Typography as="p" color="neutral-1000" variant="body-md">
                    {gettext(
                        "Event capacity is the total number of tickets you're willing to sell.",
                    )}
                </Typography>
            </Subtitle>

            <InputArea>
                <Controller
                    name="ticketsAvailable"
                    control={control}
                    rules={{
                        required: gettext(
                            'Event capacity is required',
                        ).toString(),
                        min: {
                            value: MIN_TICKETS_ALLOWED,
                            message: gettext(
                                'Capacity must be higher than 0',
                            ).toString(),
                        },
                        max: {
                            value: MAX_TICKETS_ALLOWED,
                            message: gettext(
                                'Event capacity must be between 1 and 500,000',
                            ).toString(),
                        },
                    }}
                    render={({ field, fieldState: { error } }) => (
                        <TextField
                            aria-label={gettext('Total capacity').toString()}
                            data-testid={TicketsAvailableFormFieldTestId.Field}
                            hasError={!!error}
                            helperText={error && error.message}
                            id="auto-create-form-tickets-available"
                            inputMode="numeric"
                            label={gettext('Total capacity').toString()}
                            placeholder="0"
                            {...field}
                            value={field.value || ''}
                            onChange={(value) => {
                                field.onChange({
                                    target: { value },
                                });
                            }}
                            step={1}
                            type="number"
                            validationAdornment={getFormValidationAdornment({
                                hasError: !!error,
                            })}
                            required
                            onKeyPress={(e) => {
                                if (e.key === '.' || e.key === ',') {
                                    e.preventDefault();
                                }
                            }}
                        />
                    )}
                />
            </InputArea>
        </>
    );
};

const Title = styled.div`
    margin-bottom: 8px;
`;

const Subtitle = styled.section`
    margin-bottom: 24px;
`;

const InputArea = styled.div`
    max-width: 180px;
    margin-bottom: 20px;
`;

//TODO: Remove this style when P&P will have added it inline alert variant.
